import { useMemo, useState } from "react";

import { CourseGradeCard } from "../CourseGradeCard";

import { CourseAssessmentStudentView } from "./CourseAssessmentStudentView";
import { CourseAssessmentView } from "./CourseAssessmentView";

import { t } from "i18n/i18n";
import { DropdownMenu } from "mds/components/DropdownMenu";
import { IconText } from "mds/components/IconText";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";
import { PersonClassIcon, PersonSingleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourse,
  selectCurrentUserId,
  selectUsersWhoCanParticipateInCourse,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

const InstructorAssessmentView = ({ courseGradeEnabled }: { courseGradeEnabled: boolean }) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const studentUsers = useListSelector(selectUsersWhoCanParticipateInCourse, PROFILE_PROPERTIES);
  const selectedUser = studentUsers.find((u) => u.id === selectedUserId) || null;

  const options = useMemo(() => {
    return [
      {
        title: t("assessment_dashboard.all.dropdown_all_students"),
        id: "",
        addDivider: true,
      },
      ...studentUsers
        .map((user) => ({
          title: getUserFullName(user),
          id: user.id,
        }))
        .toSorted(ORDER_BY_TITLE),
    ];
  }, [studentUsers]);

  const courseGrades = courseGradeEnabled ? <CourseGradeCard userId={selectedUserId} /> : null;

  const userPicker = (
    <DropdownMenu
      buttonText={
        <IconText
          iconStart={selectedUserId ? <PersonSingleIcon /> : <PersonClassIcon />}
          text={
            selectedUser
              ? getUserFullName(selectedUser)
              : t("assessment_dashboard.all.dropdown_all_students")
          }
        />
      }
      items={options}
      selectedValue={selectedUserId || ""}
      size="s"
      onSelectionChange={(item) => {
        const userId = item.id === "" ? null : item.id;
        setSelectedUserId(userId);
      }}
    />
  );

  const assessmentView = selectedUserId ? (
    <CourseAssessmentStudentView userId={selectedUserId} />
  ) : (
    <CourseAssessmentView />
  );

  return (
    <>
      {userPicker}
      {courseGrades}
      {assessmentView}
    </>
  );
};

const StudentAssessmentView = ({
  courseGradeEnabled,
  userId,
}: {
  courseGradeEnabled: boolean;
  userId: string;
}) => {
  const courseGrades = courseGradeEnabled ? <CourseGradeCard userId={userId} /> : null;

  return (
    <>
      {courseGrades}
      <CourseAssessmentStudentView userId={userId} />
    </>
  );
};

export const CourseAssessmentMainView = () => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const course = useAppSelector(selectCurrentCourse);
  const courseGradeEnabled = course?.course_grade_enabled;
  const { canParticipateInCourse, canAuthorCourse } = useCapabilities();

  if (courseGradeEnabled === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {canParticipateInCourse ? (
        <StudentAssessmentView courseGradeEnabled={courseGradeEnabled} userId={currentUserId} />
      ) : canAuthorCourse ? (
        <InstructorAssessmentView courseGradeEnabled={courseGradeEnabled} />
      ) : courseGradeEnabled ? (
        <CourseGradeCard />
      ) : null}
    </div>
  );
};
